import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  FileText,
  RefreshCw,
  Check,
  Clock,
  File,
  HardDrive,
  Search,
  Upload,
  Loader2,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { AUTH_URL } from "../../constants";

const ALLOWED_EXT = [".pdf", ".docx", ".doc", ".txt", ".md", ".csv", ".zip"];
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024; // 25 MB

const StatusBadge = ({ status }) => {
  const map = {
    approved: { bg: "bg-green-100", text: "text-green-700", icon: Check },
    pending: { bg: "bg-yellow-100", text: "text-yellow-700", icon: Clock },
    processing: { bg: "bg-blue-100", text: "text-blue-700", icon: RefreshCw },
    rejected: { bg: "bg-red-100", text: "text-red-700", icon: null },
  };
  const s = map[status] || {
    bg: "bg-slate-100",
    text: "text-slate-600",
    icon: null,
  };
  const Icon = s.icon;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}
    >
      {Icon && <Icon className="w-3 h-3 mr-1" />}
      {status}
    </span>
  );
};

const formatBytes = (bytes) => {
  if (!bytes) return "--";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function DocumentViewer() {
  const { token } = useAuth();
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState("");
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const currentFacility = facilities.find(
    (f) => f.facility_id === selectedFacility,
  );
  const canUpload = currentFacility?.self_service_upload === true;

  const fetchFacilities = useCallback(async () => {
    try {
      const res = await fetch(`${AUTH_URL}/my/facilities`, { headers });
      const data = await res.json();
      if (Array.isArray(data)) {
        setFacilities(data);
        if (data.length > 0 && !selectedFacility) {
          setSelectedFacility(data[0].facility_id);
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDocuments = useCallback(async () => {
    if (!selectedFacility) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${AUTH_URL}/my/documents?facility_id=${encodeURIComponent(selectedFacility)}`,
        { headers },
      );
      const data = await res.json();
      if (Array.isArray(data)) setDocuments(data);
      else setDocuments([]);
    } catch {
      setDocuments([]);
    }
    setLoading(false);
  }, [selectedFacility, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchFacilities();
  }, [fetchFacilities]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const uploadFiles = async (files) => {
    if (!selectedFacility) return;
    setUploading(true);
    let uploaded = 0;
    const errors = [];
    for (const file of files) {
      const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
      if (!ALLOWED_EXT.includes(ext)) {
        errors.push(`${file.name}: unsupported type`);
        continue;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        errors.push(
          `${file.name}: ${(file.size / 1024 / 1024).toFixed(1)}MB exceeds ${MAX_UPLOAD_BYTES / 1024 / 1024}MB limit`,
        );
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("facility_id", selectedFacility);
        const res = await fetch(`${AUTH_URL}/rag/documents/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const result = await res.json();
        if (result.success) {
          uploaded += result.uploaded_count || 1;
        } else {
          errors.push(`${file.name}: ${result.error}`);
        }
      } catch (err) {
        errors.push(`${file.name}: ${err.message}`);
      }
    }
    setUploading(false);
    if (errors.length > 0) alert(errors.join("\n"));
    if (uploaded > 0) fetchDocuments();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (canUpload) uploadFiles(Array.from(e.dataTransfer.files));
  };
  const handleFileSelect = (e) => {
    uploadFiles(Array.from(e.target.files));
    e.target.value = "";
  };

  const filtered = documents.filter(
    (d) =>
      !search ||
      d.filename?.toLowerCase().includes(search.toLowerCase()) ||
      d.doc_type?.toLowerCase().includes(search.toLowerCase()),
  );

  const stats = {
    total: documents.length,
    approved: documents.filter((d) => d.status === "approved").length,
    totalSize: documents.reduce((sum, d) => sum + (d.file_size_bytes || 0), 0),
    totalChunks: documents.reduce((sum, d) => sum + (d.chunk_count || 0), 0),
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              Knowledge Base Documents
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              Documents powering your AI assistant
            </p>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={selectedFacility}
              onChange={(e) => setSelectedFacility(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
            >
              {facilities.map((f) => (
                <option key={f.facility_id} value={f.facility_id}>
                  {f.facility_name || f.display_name || f.facility_id}
                </option>
              ))}
            </select>

            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter documents..."
                className="pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm w-52"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Upload zone (only when self_service_upload is enabled) */}
      {canUpload && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
            dragOver
              ? "border-blue-500 bg-blue-50"
              : "border-slate-300 bg-white hover:border-blue-400 hover:bg-slate-50"
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ALLOWED_EXT.join(",")}
            onChange={handleFileSelect}
            className="hidden"
          />
          {uploading ? (
            <div className="flex items-center justify-center gap-2 text-blue-600">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm font-medium">Uploading...</span>
            </div>
          ) : (
            <>
              <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-sm text-slate-600">
                Drag and drop documents here, or click to browse
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Supported: {ALLOWED_EXT.join(", ")} · max{" "}
                {MAX_UPLOAD_BYTES / 1024 / 1024}MB per file · .zip for bulk
              </p>
            </>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-blue-500" />
            <span className="text-xs text-slate-500">Total Documents</span>
          </div>
          <p className="text-xl font-bold text-slate-900">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Check className="w-4 h-4 text-green-500" />
            <span className="text-xs text-slate-500">Approved</span>
          </div>
          <p className="text-xl font-bold text-slate-900">{stats.approved}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <HardDrive className="w-4 h-4 text-purple-500" />
            <span className="text-xs text-slate-500">Total Size</span>
          </div>
          <p className="text-xl font-bold text-slate-900">
            {formatBytes(stats.totalSize)}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <File className="w-4 h-4 text-amber-500" />
            <span className="text-xs text-slate-500">Total Chunks</span>
          </div>
          <p className="text-xl font-bold text-slate-900">
            {stats.totalChunks.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Document List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <FileText className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm">No documents found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">
                    Filename
                  </th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">
                    Type
                  </th>
                  <th className="text-center py-3 px-4 text-slate-500 font-medium">
                    Status
                  </th>
                  <th className="text-right py-3 px-4 text-slate-500 font-medium">
                    Size
                  </th>
                  <th className="text-right py-3 px-4 text-slate-500 font-medium">
                    Chunks
                  </th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">
                    Uploaded
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((doc) => (
                  <tr
                    key={doc.id}
                    className="border-b border-slate-50 hover:bg-slate-50"
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <FileText className="w-4 h-4 text-slate-400 mr-2 flex-shrink-0" />
                        <span className="font-medium text-slate-800 truncate max-w-[300px]">
                          {doc.filename}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-600">
                      {doc.doc_type || "--"}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <StatusBadge status={doc.status} />
                    </td>
                    <td className="py-3 px-4 text-right text-slate-600">
                      {formatBytes(doc.file_size_bytes)}
                    </td>
                    <td className="py-3 px-4 text-right text-slate-600">
                      {doc.chunk_count || "--"}
                    </td>
                    <td className="py-3 px-4 text-slate-600">
                      {doc.uploaded_at
                        ? new Date(doc.uploaded_at).toLocaleDateString()
                        : doc.created_at
                          ? new Date(doc.created_at).toLocaleDateString()
                          : "--"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
